"use client"

import { useState, useEffect, useRef } from "react"
import { useRouter } from "next/navigation"
import Link from "next/link"
import { motion } from "framer-motion"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { Moon, Sun, Pencil } from "lucide-react"
import { db } from "@/lib/instantdb/client"
import { IS_MOCK_MODE } from "@/lib/instantdb/mode"
import { saveStudentPreferences, StudentPreferences } from "@/lib/preferences"
import { BackgroundPaths } from "@/components/ui/background-paths"
import { TextScramble } from "@/components/ui/text-scramble"

const PRIVILEGED_EMAILS: Record<string, { role: string; tier: string }> = {
  "gelowushu8z@icloud.com": { role: "teacher", tier: "lifetime" },
  "wizardwyshu@gmail.com": { role: "admin", tier: "lifetime" },
  "builderssociety9@gmail.com": { role: "teacher", tier: "lifetime" },
  "gelowushu@gmail.com": { role: "student", tier: "gold" },
  "student@wshu.com": { role: "student", tier: "silver" },
  "free@example.com": { role: "free", tier: "free" },
}

const normalizeEmail = (value: string) => value.trim().toLowerCase()
const slugify = (value: string) => value.replace(/[^a-z0-9]/g, "-")

const deriveRoleAndTier = (email: string, accessKey?: string) => {
  // SECURITY: Only privileged emails can be teachers/admins
  // Access keys can ONLY grant student tiers, NEVER teacher/admin access
  
  // Check if this is a privileged email first
  const isPrivileged = PRIVILEGED_EMAILS.hasOwnProperty(email)
  
  if (isPrivileged) {
    // Privileged emails ALWAYS get their hardcoded role - no exceptions
    return {
      role: PRIVILEGED_EMAILS[email].role,
      tier: PRIVILEGED_EMAILS[email].tier
    }
  }
  
  // Non-privileged emails: Default to "student" role (never teacher/admin)
  let role = "student"
  let tier = "free"

  // Access keys can ONLY upgrade TIER for students, NEVER grant teacher/admin roles
  if (accessKey) {
    const keyUpper = accessKey.toUpperCase()
    
    // SECURITY: LIFETIME keys are DISABLED - removed to prevent privilege escalation
    // Only GOLD, SILVER, BRONZE keys work, and they only upgrade TIER, not ROLE
    
    if (keyUpper.startsWith("GOLD-")) {
      tier = "gold"
    } else if (keyUpper.startsWith("SILVER-")) {
      tier = "silver"
    } else if (keyUpper.startsWith("BRONZE-")) {
      tier = "bronze"
    } else if (keyUpper.startsWith("LIFETIME-")) {
      // EXPLICITLY BLOCK LIFETIME keys - log security warning
      console.warn(`🚨 SECURITY: Attempted to use LIFETIME key by ${email} - BLOCKED`)
      tier = "free" // Keep as free, don't grant any access
    }
    // Any other key format is ignored
  }

  // SECURITY: Double-check that we never return teacher/admin for non-privileged users
  if (role === "teacher" || role === "admin") {
    console.error(`🚨 SECURITY BREACH PREVENTED: Almost granted ${role} to ${email}`)
    role = "student"
  }

  return { role, tier }
}

const isSchemaMissingError = (error: any) => {
  const msg = error?.message || ""
  return (
    typeof msg === "string" &&
    (msg.toLowerCase().includes("entity 'users' does not exist") ||
     msg.toLowerCase().includes("object stores") ||
     msg.toLowerCase().includes("idbdatabase"))
  )
}

type MagicCodeVerifyResponse = {
  success: boolean
  refreshToken?: string
  instantUserId?: string
  message?: string
}

const MAGIC_CODE_REQUEST_PATH = "/api/auth/otp/request"
const MAGIC_CODE_VERIFY_PATH = "/api/auth/otp/verify"

async function postMagicRequest<T>(url: string, payload: Record<string, any>): Promise<T> {
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  })

  let data: T & { error?: string }
  try {
    data = await response.json()
  } catch {
    data = {} as T & { error?: string }
  }

  if (!response.ok) {
    throw new Error(data?.error || "InstantDB magic code endpoint failed")
  }

  return data
}

const requestServerMagicCode = (email: string) =>
  postMagicRequest<{ success: boolean; mock?: boolean }>(MAGIC_CODE_REQUEST_PATH, { email })

const verifyServerMagicCode = (email: string, code: string, metadata?: Record<string, any>) =>
  postMagicRequest<MagicCodeVerifyResponse>(MAGIC_CODE_VERIFY_PATH, {
    email,
    code,
    ...metadata,
  })

export default function LoginPage() {
  const router = useRouter()
  const [email, setEmail] = useState("")
  const [isMember, setIsMember] = useState(false)
  const [accessKey, setAccessKey] = useState("")
  const [otp, setOtp] = useState("")
  const [step, setStep] = useState<"email" | "accessKey" | "otp">("email")
  const [loading, setLoading] = useState(false)
  const [loginStage, setLoginStage] = useState<"idle" | "login" | "signup">("idle")
  const [loggingIn, setLoggingIn] = useState(false)
  const [isDarkMode, setIsDarkMode] = useState(false)
  const [error, setError] = useState<string>("")
  const [resendCountdown, setResendCountdown] = useState(0)
  const emailInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    // Check saved theme preference
    const savedTheme = localStorage.getItem('theme')
    if (savedTheme === 'dark') {
      setIsDarkMode(true)
      document.documentElement.classList.add('dark')
    }

    // Auto-fill email from sessionStorage if coming from signup
    const savedEmail = sessionStorage.getItem("login-email")
    if (savedEmail) {
      setEmail(savedEmail)
      sessionStorage.removeItem("login-email")
    }
  }, [])

  // Countdown timer for resend code
  useEffect(() => {
    if (resendCountdown <= 0) return
    
    const timer = setInterval(() => {
      setResendCountdown((prev) => prev - 1)
    }, 1000)
    
    return () => clearInterval(timer)
  }, [resendCountdown])

  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode)
    if (!isDarkMode) {
      document.documentElement.classList.add('dark')
      localStorage.setItem('theme', 'dark')
    } else {
      document.documentElement.classList.remove('dark')
      localStorage.setItem('theme', 'light')
    }
  }

  const handleSendOTP = async (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }
    
    // Prevent double-clicks
    if (loading) {
      console.log("⚠️ [LOGIN] Already sending OTP, ignoring click")
      return
    }
    
    const currentEmail = email || emailInputRef.current?.value || ""
    if (!currentEmail.trim()) {
      setError("Please enter your email to receive a code.")
      return
    }
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(currentEmail.trim())) {
      setError("Please enter a valid email address.")
      return
    }
    
    console.log("📧 [LOGIN] Sending OTP to:", currentEmail.trim().toLowerCase())
    setLoading(true)
    setError("")
    
    const normalizedEmail = currentEmail.trim().toLowerCase()
    if (!email) {
      setEmail(currentEmail.trim())
    }
    
    try {
      let result: { code?: string; sent?: true } | { success: boolean }
      if (IS_MOCK_MODE) {
        result = await db.auth.sendMagicCode({ email: normalizedEmail })
      } else {
        // Try server first
        try {
          result = await requestServerMagicCode(normalizedEmail)
        } catch (primaryErr: any) {
          // Check if it's a rate limit error
          const errorMsg = primaryErr?.message || ""
          const isRateLimit = errorMsg.toLowerCase().includes("too many") || 
                             errorMsg.toLowerCase().includes("rate limit") ||
                             errorMsg.toLowerCase().includes("wait")
          
          if (isRateLimit) {
            // Don't fallback for rate limit - show error to user
            throw primaryErr
          }
          
          console.warn("⚠️ [LOGIN] Primary send failed, falling back to client mock:", primaryErr)
          // Fallback to client mock to unblock user even if IndexedDB is unhappy
          result = await db.auth.sendMagicCode({ email: normalizedEmail })
        }
      }
      
      console.log("=".repeat(50))
      console.log(`🔐 Magic code sent to ${normalizedEmail}`)
      console.log(`   Check your email for the 6-digit code`)
      console.log("=".repeat(50))
      
      // In development, InstantDB may return the code for testing
      const returnedCode = (result as { code?: string }).code
      if (returnedCode) {
        console.log(`🔐 DEV CODE: ${returnedCode}`)
      }
      
      setStep("otp")
      setResendCountdown(15) // Start 15 second countdown
    } catch (error: any) {
      console.error("❌ Failed to send magic code:", error)
      let errorMessage = error?.message || error?.toString() || "Failed to send verification code. Please try again."
      
      // Convert technical errors to user-friendly messages
      if (errorMessage.toLowerCase().includes("malformed") || 
          errorMessage.toLowerCase().includes("invalid email") ||
          errorMessage.toLowerCase().includes("parameter")) {
        errorMessage = "Please enter a valid email address."
      } else if (errorMessage.toLowerCase().includes("too many") || 
                 errorMessage.toLowerCase().includes("rate limit")) {
        // Keep the rate limit message as-is since it includes wait time
        // errorMessage already has the proper message from the API
      } else if (normalizedEmail.includes('@icloud.com')) {
        // Special handling for iCloud emails
        errorMessage = "⚠️ iCloud email detected. If you don't receive the code within 2 minutes, please try using a Gmail or other email provider instead. iCloud may have delivery delays."
      }
      
      setError(errorMessage)
      // Don't progress to OTP step if there was an error - let user retry
      console.error("❌ [LOGIN] Error details:", {
        message: errorMessage,
        error: error,
        stack: error?.stack
      })
    } finally {
      setLoading(false)
    }
  }

  const handleResendCode = async () => {
    if (resendCountdown > 0) return
    await handleSendOTP()
  }

  const handleAccessKeySubmit = () => {
    if (accessKey) {
      // Validate access key (mock)
      if (accessKey.length >= 8) {
        setStep("otp")
        handleSendOTP()
      }
    }
  }

  const handleEditEmail = () => {
    setError("")
    setOtp("")
    setStep("email")
  }

  const syncUserProfile = async (
    instantUserId: string,
    normalizedEmail: string,
    role: string,
    tier: string,
    accessKeyValue?: string
  ) => {
    try {
    // If InstantDB schema or client methods are missing, treat as schema missing
    if (IS_MOCK_MODE) return null
      const clientDb = db as any
    if (typeof clientDb.query !== "function" || typeof clientDb.transact !== "function") {
      throw new Error("Schema missing or client not ready")
    }
      const { data } = await clientDb.query({
        users: {
          $: {
            where: { email: normalizedEmail },
          },
        },
      })

      const existingUser = data?.users?.[0]
      const recordId = existingUser?.id || slugify(normalizedEmail)
      const now = Date.now()

      // IMPORTANT: Privileged emails (in PRIVILEGED_EMAILS list) always get their hardcoded role
      // For other users, preserve their original role to prevent tier changes via access keys
      const isPrivilegedEmail = PRIVILEGED_EMAILS.hasOwnProperty(normalizedEmail)
      const finalRole = isPrivilegedEmail ? role : (existingUser?.role || role)
      const finalTier = isPrivilegedEmail ? tier : (existingUser?.tier || tier)

      // Log if updating a privileged email
      if (existingUser && isPrivilegedEmail && existingUser.role !== role) {
        console.log(`✨ [LOGIN] Updating privileged email ${normalizedEmail} from ${existingUser.role} to ${role}`)
      }

      // Log if someone tried to use a different access key
      if (existingUser && accessKeyValue && existingUser.tier !== tier && !isPrivilegedEmail) {
        console.warn(`⚠️ [LOGIN] User ${normalizedEmail} tried to use access key for ${tier} tier but already has ${existingUser.tier} tier. Keeping original tier.`)
      }

      await clientDb.transact([
        clientDb.tx["$users"][recordId].update({
          email: normalizedEmail,
          role: finalRole,
          tier: finalTier,
          // Only store access key for NEW users, don't overwrite existing
          ...(existingUser ? {} : { accessKey: accessKeyValue || undefined }),
          instantUserId,
          lastLogin: now,
          ...(existingUser ? {} : { createdAt: now, signupDate: now }),
        }),
      ])
      return {
        recordId,
        preferredName: existingUser?.preferredName ?? existingUser?.name ?? null,
        preferredTimezone: existingUser?.preferredTimezone ?? null,
        preferredStyle: existingUser?.preferredStyle ?? null,
        preferencesCompletedAt: existingUser?.preferencesCompletedAt ?? null,
        // Return the actual role/tier being used (for localStorage update)
        actualRole: finalRole,
        actualTier: finalTier,
        isExistingUser: !!existingUser,
      }
    } catch (syncError) {
      console.warn("⚠️ [LOGIN] Failed to sync user profile:", syncError)
      // Return null for any error, including schema missing - the caller will handle it
      return null
    }
  }

  const handleOTPSubmit = async () => {
    if (loggingIn) return // Prevent double-clicks
    
    setLoading(true)
    setLoggingIn(true)
    setError("")
    
    try {
      const normalizedEmail = normalizeEmail(email)
      let trimmedAccessKey = accessKey.trim()
      console.log("🔐 [LOGIN] Verifying magic code:", { email: normalizedEmail, otp })
      
      // Skip user check to improve performance - assume new user for faster login flow
      // User will be created/updated during verification anyway
      let isNewUser = true
      
      // Set login stage based on whether user exists
      setLoginStage(isNewUser ? "signup" : "login")
      
      let authResult: { user?: { id: string } } | null = null
      let schemaMissing = false
      let schemaErrorMessage: string | null = null

      if (IS_MOCK_MODE) {
        authResult = await db.auth.signInWithMagicCode({
          email: normalizedEmail,
          code: otp.trim(),
        })
      } else {
        try {
          const verifyResponse = await verifyServerMagicCode(
            normalizedEmail,
            otp.trim(),
            trimmedAccessKey ? { accessKey: trimmedAccessKey } : undefined,
          )

          if (!verifyResponse?.refreshToken) {
            throw new Error(verifyResponse?.message || "InstantDB did not return a session token")
          }

          const authClient = db as any
          const signInWithToken =
            authClient?.auth?.signInWithToken || authClient?.auth?.signInWithCustomToken
          if (typeof signInWithToken !== "function") {
            throw new Error("InstantDB client missing signInWithToken support")
          }
          try {
            authResult = await signInWithToken.call(authClient.auth, verifyResponse.refreshToken)
          } catch (indexedDBError: any) {
            // IndexedDB errors shouldn't block auth - user is authenticated via refreshToken
            if (indexedDBError?.message?.includes("IDBDatabase") || 
                indexedDBError?.message?.includes("object stores") ||
                indexedDBError?.name === "NotFoundError") {
              console.warn("⚠️ [LOGIN] IndexedDB error (non-critical), continuing with auth:", indexedDBError.message)
              // Create auth result manually since signInWithToken failed due to IndexedDB
              // The refreshToken proves authentication, so we can proceed
              authResult = { 
                user: { 
                  id: verifyResponse.instantUserId || `user-${Date.now()}`
                } 
              } as { user: { id: string } }
            } else {
              throw indexedDBError
            }
          }
        } catch (verifyErr: any) {
          if (isSchemaMissingError(verifyErr)) {
            schemaMissing = true
            schemaErrorMessage = "User schema is missing in InstantDB; continuing in local mode."
            // Fallback: create a mock auth result so we can proceed to signup flow
            authResult = { user: { id: `local-${normalizedEmail}` } }
          } else {
            throw verifyErr
          }
        }
      }

      const signedInUser = authResult?.user
      if (!signedInUser?.id) {
        throw new Error("Invalid verification code")
      }

      console.log("✅ [LOGIN] Magic code verified, user signed in:", signedInUser.id)

      // Derive role/tier locally
      let { role, tier } = deriveRoleAndTier(normalizedEmail, trimmedAccessKey)

      // Fetch server-stored role/tier to honor admin/teacher updates
      try {
        const roleRes = await fetch("/api/auth/user-role", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: normalizedEmail }),
        })
        if (roleRes.ok) {
          const payload = await roleRes.json()
          if (payload?.found) {
            role = payload.role || role
            tier = payload.tier || tier
            if (payload.accessKey) {
              trimmedAccessKey = payload.accessKey
            }
            console.log("✅ [LOGIN] Using server role/tier:", role, tier)
          }
        }
      } catch (e) {
        console.warn("⚠️ [LOGIN] Failed to fetch server role/tier, using derived:", e)
      }

      // Check if user has paid access (has member key OR paid tier)
      const hasPaidAccess = tier !== "free" || trimmedAccessKey !== ""

      // Store session IMMEDIATELY - don't wait for anything
      sessionStorage.setItem("user-email", normalizedEmail)
      localStorage.setItem("user-email", normalizedEmail)
      sessionStorage.setItem("user-role", role)
      localStorage.setItem("user-role", role)
      sessionStorage.setItem("user-tier", tier)
      localStorage.setItem("user-tier", tier)
      // Store access key if provided
      if (trimmedAccessKey) {
        sessionStorage.setItem("user-access-key", trimmedAccessKey)
        localStorage.setItem("user-access-key", trimmedAccessKey)
      }

      console.log("✅ [LOGIN] Stored role:", role, "tier:", tier, "hasPaidAccess:", hasPaidAccess)

      // Keep "Getting You Logged In" screen visible
      setLoading(false)

      // Determine redirect path based on authorization
      let redirectPath = "/onboarding"
      
      if (role === "teacher") {
        redirectPath = "/teacher/dashboard"
      } else if (role === "admin") {
        redirectPath = "/admin/dashboard"
      } else if (role === "student") {
        // Student role: check if they have paid access
        if (!hasPaidAccess) {
          // No member key and free tier - redirect to free resources
          redirectPath = "/dashboard"
        } else {
          // Has member key or paid tier - allow access to student dashboard
          redirectPath = "/student/dashboard"
        }
      } else if (role === "free") {
        // Free role always goes to free dashboard
        redirectPath = "/dashboard"
      } else if (isNewUser && role === "free") {
        // New user signing up - redirect to free dashboard
        redirectPath = "/dashboard"
      }

      // IMPORTANT: Register user via server API BEFORE navigation to ensure signupDate is recorded
      try {
        console.log("📝 [LOGIN] Registering user via server API...")
        const registerResponse = await fetch("/api/auth/register", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: normalizedEmail,
            role,
            instantUserId: signedInUser.id,
            source: "login",
            signupReferrer: typeof document !== "undefined" ? document.referrer : "",
            signupUserAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
          }),
        })
        
        if (registerResponse.ok) {
          const registerData = await registerResponse.json()
          console.log("✅ [LOGIN] User registered via server API:", registerData)
        } else {
          const errorText = await registerResponse.text()
          console.warn("⚠️ [LOGIN] Server registration failed:", errorText)
        }
      } catch (registerErr) {
        console.warn("⚠️ [LOGIN] Server registration error (non-critical):", registerErr)
      }

      // Navigate after registration is complete
      console.log("🚀 [LOGIN] Navigating to:", redirectPath, isNewUser ? "(new user)" : "(existing user)")
      router.replace(redirectPath)

      // Do profile sync in background (fire and forget) - less critical
      Promise.resolve().then(async () => {
        try {
          if (!schemaMissing) {
            const syncedProfile = await syncUserProfile(
              signedInUser.id,
              normalizedEmail,
              role,
              tier,
              trimmedAccessKey || undefined
            )
            if (syncedProfile) {
              // If this is an existing user, update localStorage with their ACTUAL role/tier
              // This ensures the access key doesn't override their real tier
              if (syncedProfile.isExistingUser && syncedProfile.actualRole && syncedProfile.actualTier) {
                sessionStorage.setItem("user-role", syncedProfile.actualRole)
                localStorage.setItem("user-role", syncedProfile.actualRole)
                sessionStorage.setItem("user-tier", syncedProfile.actualTier)
                localStorage.setItem("user-tier", syncedProfile.actualTier)
                console.log("✅ [LOGIN] Updated storage with existing user's tier:", syncedProfile.actualTier)
              }
              
              const preferenceSeed: StudentPreferences = {}
              if (syncedProfile.preferredName) {
                preferenceSeed.name = syncedProfile.preferredName
              }
              if (syncedProfile.preferredTimezone) {
                preferenceSeed.timezone = syncedProfile.preferredTimezone
              }
              if (syncedProfile.preferredStyle) {
                preferenceSeed.tradingStyle = syncedProfile.preferredStyle
              }
              if (syncedProfile.preferencesCompletedAt) {
                preferenceSeed.completedAt = syncedProfile.preferencesCompletedAt
              }
              if (Object.keys(preferenceSeed).length > 0) {
                try {
                  await saveStudentPreferences(preferenceSeed, { userId: signedInUser.id })
                } catch (prefErr) {
                  console.warn("⚠️ [LOGIN] Preference save failed:", prefErr)
                }
              }
            }
          }
        } catch (syncErr) {
          console.warn("⚠️ [LOGIN] Background profile sync failed (non-critical):", syncErr)
        }
      })
    } catch (error: any) {
      console.error("❌ [LOGIN] Failed to verify code:", error)
      
      // Handle timeout errors - redirect to signup for unregistered users
      if (error?.message?.includes("timeout") || error?.message?.includes("User check")) {
        console.log("⚠️ [LOGIN] User check timed out, redirecting unregistered user to signup")
        setLoggingIn(false)
        setLoginStage("idle")
        sessionStorage.setItem("signup-email", email)
        router.push("/signup")
        return
      }
      
      if (isSchemaMissingError(error)) {
        setError("Login schema is not ready yet. Please contact support.")
      } else {
        setError(error.message || "Invalid verification code. Please try again.")
      }
      setLoggingIn(false)
      setLoginStage("idle")
    } finally {
      setLoading(false)
    }
  }

  // Show "Getting You Logged In" / "Getting You Signed Up" screen during login process
  if (loggingIn) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-background">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
          className="text-center space-y-6"
        >
          <div className="relative">
            <div className="w-16 h-16 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
          </div>
          <div className="space-y-2">
            <h2 className="text-2xl font-bold text-foreground">
              {loginStage === "signup" ? "Getting You Signed Up" : "Getting You Logged In"}
            </h2>
            <p className="text-muted-foreground">
              {loginStage === "signup"
                ? "Setting up your profile and preferences..."
                : "Please wait while we set up your account..."}
            </p>
          </div>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background relative overflow-hidden">
      {/* Animated Background Paths */}
      <BackgroundPaths />
      
      {/* Logo - Top Left */}
      <Link href="/" className="fixed top-4 left-4 z-50">
        <div className="relative w-12 h-auto flex items-center justify-center">
          <img
            src="/brand/shapp-dragon-light.png"
            alt="Shapp Logo"
            className="w-full h-auto object-contain drop-shadow-lg hover:drop-shadow-xl transition-all duration-200 hover:scale-105"
            style={{ maxHeight: '3rem' }}
              />
            </div>
            </Link>
      
      {/* Dark Mode Toggle */}
      <button
        onClick={toggleDarkMode}
        className="fixed top-4 right-4 p-3 rounded-full bg-white dark:bg-gray-800 shadow-lg hover:shadow-xl transition-all duration-200 z-50 border border-gray-200 dark:border-gray-700"
        aria-label="Toggle dark mode"
      >
        {isDarkMode ? (
          <Sun className="w-5 h-5 text-yellow-500" />
        ) : (
          <Moon className="w-5 h-5 text-gray-700 dark:text-gray-300" />
        )}
      </button>

      <motion.div
        initial={{ opacity: 1, y: 0 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md relative z-10"
      >
        <Card>
          <CardHeader>
            <CardTitle className="text-3xl">
              <TextScramble text="Welcome to shapp" blurInitially={true} />
            </CardTitle>
            <CardDescription>W&apos;SHU EMPIRE Educational Platform</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {error && (
              <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-600 dark:text-red-400 text-sm">
                {error}
              </div>
            )}
            {step === "email" && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    ref={emailInputRef}
                    autoComplete="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault()
                        if (isMember) {
                          handleAccessKeySubmit()
                        } else {
                          handleSendOTP()
                        }
                      }
                    }}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="member">W&apos;SHU Member?</Label>
                  <Switch
                    id="member"
                    checked={isMember}
                    onCheckedChange={setIsMember}
                  />
                </div>
                {isMember && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    className="space-y-2"
                  >
                    <Label htmlFor="accessKey">Access Key</Label>
                    <Input
                      id="accessKey"
                      type="text"
                      placeholder="Enter your access key"
                      value={accessKey}
                      onChange={(e) => setAccessKey(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault()
                          handleAccessKeySubmit()
                        }
                      }}
                    />
                    <Button
                      onClick={handleAccessKeySubmit}
                      className="w-full"
                      disabled={!accessKey}
                    >
                      Continue
                    </Button>
                  </motion.div>
                )}
                {!isMember && (
                <Button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    const emailValue = email || emailInputRef.current?.value || ""
                    console.log("🔘 [LOGIN] Send OTP button clicked, email:", emailValue)
                    if (!emailValue.trim()) {
                      setError("Please enter your email to receive a code.")
                      return
                    }
                    handleSendOTP(e)
                  }}
                  className="w-full"
                  disabled={loading}
                >
                  {loading ? "Sending..." : "Send OTP"}
                </Button>
                )}
              </>
            )}

            {step === "otp" && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-4"
              >
                <div className="flex items-center justify-between rounded-xl border border-muted bg-muted/30 px-4 py-3">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">
                      Code sent to
                    </p>
                    <p className="font-medium text-sm">{email}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleEditEmail}
                    aria-label="Edit email address"
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="otp">Enter OTP</Label>
                  <Input
                    id="otp"
                    type="text"
                  placeholder="Enter the full code"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault()
                        handleOTPSubmit()
                      }
                    }}
                  />
              </div>
                <Button
                  onClick={handleOTPSubmit}
                  className="w-full"
                  disabled={!otp || loading || loggingIn}
                >
                  {loading || loggingIn ? "Verifying..." : "Verify & Login"}
                </Button>
                
                {/* Resend code option */}
                <div className="text-center text-sm font-medium mt-2 flex flex-col items-center gap-1">
                  <button
                    onClick={handleResendCode}
                    disabled={resendCountdown > 0 || loading}
                    className={`transition-colors duration-300 ${
                      resendCountdown > 0
                        ? "text-emerald-300/60 cursor-not-allowed"
                        : "text-emerald-400 hover:text-emerald-300 hover:underline cursor-pointer"
                    }`}
                  >
                    Didn&apos;t Receive It? Send New Code
                  </button>
                  {resendCountdown > 0 && (
                    <span className="text-emerald-200 text-xs">
                      Resend available in {resendCountdown}s
                    </span>
                  )}
                </div>
                
                <Button
                  variant="ghost"
                  onClick={() => setStep("email")}
                  className="w-full"
                >
                  Back
                </Button>
              </motion.div>
            )}

            {step === "email" && (
              <div className="mt-4 text-center text-sm text-muted-foreground">
                Don&apos;t have an account?{" "}
                <Link
                  href="/signup"
                  onClick={() => {
                    if (email) {
                      sessionStorage.setItem("signup-email", email)
                    }
                  }}
                  className="text-accent-teal hover:underline"
                >
                  Create free account
                </Link>
          </div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  )
}
